import React from 'react';
import { useRouter } from 'next/router';

import { useHeading } from 'hooks/useHeading';
import { ManageHeadingTag } from 'ui/content/ContentHeading';
export interface ManagedHeadingProps {
  tag: ManageHeadingTag | string | null;
  mainHeading?: boolean;
  dataTestId?: string;
  children?: React.ReactNode;
  dangerouslySetInnerHTML?: { __html: string };
  [key: string]: any;
}

const renderHeading = (
  tag: string,
  content: React.ReactNode,
  attributes?: any,
  dataTestId?: string
) => {
  const contentWithoutHTag = React.Children.map(content, child => {
    if (
      React.isValidElement(child) &&
      ['h1', 'h2', 'h3', 'h4'].includes(child.type as any)
    ) {
      return child.props.children;
    }
    return child;
  });

  return React.createElement(
    tag,
    { ...attributes, 'data-test-id': dataTestId },
    contentWithoutHTag
  );
};

const flatten = (children, flat: any[] = []) => {
  flat = [...flat, ...React.Children.toArray(children)];

  if (children?.props && children?.props?.children) {
    return flatten(children?.props?.children, flat);
  }

  return flat;
};

const simplify = children => {
  const flat = flatten(children);

  return flat.map(({ key, ref, type, props }) => {
    const childProps = props || {};

    return {
      key,
      ref,
      type,
      props: childProps,
    };
  });
};

const compareObjs = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

export const ManagedHeading = ({
  tag,
  mainHeading,
  children,
  dataTestId,
  dangerouslySetInnerHTML,
  ...props
}: ManagedHeadingProps) => {
  const { headingRef, isPLPref } = useHeading();
  const router = useRouter();

  const DEFAULT_HEADING_TAG = 'h4';
  const headingTag = tag ?? DEFAULT_HEADING_TAG;

  const isPDP = router?.route === '/[country]/[language]/pd/[model]/[id]';
  const isPLP =
    isPLPref.current && router?.route === '/[country]/[language]/[...category]';
  const isPDPorPLP = isPDP || isPLP;

  const simpleChildren =
    typeof children === 'string' ? children : simplify(children);

  if (headingTag === 'h1') {
    if (
      (!isPDPorPLP &&
        (!headingRef.current ||
          compareObjs(simpleChildren, headingRef.current))) ||
      (isPDPorPLP && mainHeading)
    ) {
      headingRef.current = simpleChildren;

      return renderHeading(
        'h1',
        dangerouslySetInnerHTML ? null : children,
        { ...props, dangerouslySetInnerHTML },
        dataTestId
      );
    }
    return renderHeading(
      'h2',
      dangerouslySetInnerHTML ? null : children,
      { ...props, dangerouslySetInnerHTML },
      dataTestId
    );
  }

  return renderHeading(
    headingTag,
    dangerouslySetInnerHTML ? null : children,
    { ...props, dangerouslySetInnerHTML },
    dataTestId
  );
};
